import { z } from 'zod';
import { PaginationOffsetSchema } from './shared/pagination.js';

export const PulseVerifiedTypeSchema = z.enum(['none', 'blue', 'business', 'government']);

export const PulseProfileChangeSchema = z.object({
  id: z.string(),
  profileId: z.string(),
  oldUsername: z.string().nullable(),
  oldName: z.string().nullable(),
  oldDescription: z.string().nullable(),
  oldLocation: z.string().nullable(),
  oldProfileImageUrl: z.string().nullable(),
  oldUrl: z.string().nullable(),
  oldVerifiedType: PulseVerifiedTypeSchema.nullable(),
  oldFollowersCount: z.number().nullable(),
  oldFollowingCount: z.number().nullable(),
  oldPinnedTweetId: z.string().nullable(),
  username: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  profileImageUrl: z.string(),
  profileBannerUrl: z.string().nullable(),
  url: z.string().nullable(),
  verifiedType: PulseVerifiedTypeSchema,
  followersCount: z.number(),
  followingCount: z.number(),
  isFollowing: z.boolean(),
  followsYou: z.boolean(),
  pinnedTweetId: z.string().nullable(),
  detectedAt: z.date(),
  changeSignificance: z.number(),
});

export type PulseProfileChange = z.infer<typeof PulseProfileChangeSchema>;

export const PulseChangesResponseSchema = z.object({
  changes: z.array(PulseProfileChangeSchema),
  total: z.number(),
  days: z.number(),
  significance: z.number(),
});

export type PulseChangesResponse = z.infer<typeof PulseChangesResponseSchema>;

export const PulseProfileChangesRequestSchema = z.object({
  profileIds: z
    .array(z.union([z.string(), z.number()]))
    .min(1, 'At least one profileId is required')
    .max(10_000, 'Too many profile IDs (max 10_000)'),
  days: z.number().int().min(1).max(365).optional(),
  significance: z.number().int().min(1).max(100).optional(),
});

export type PulseProfileChangesRequest = z.infer<typeof PulseProfileChangesRequestSchema>;

export const PulseProfileChangesQuerySchema = z.object({
  days: z.coerce.number().int().min(1).max(365).optional(),
  significance: z.coerce.number().int().min(1).max(100).optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  cursorDetectedAt: z.string().optional(),
  cursorProfileId: z.string().optional(),
  excludeFollowerChanges: z.string().optional(),
  excludeProfileImageOnly: z.string().optional(),
  search: z.string().optional(),
});

export type PulseProfileChangesQuery = z.infer<typeof PulseProfileChangesQuerySchema>;

export const PulseSettingsSchema = z.object({
  enabled: z.boolean(),
  emailEnabled: z.boolean(),
  scheduleHour: z.number(),
  maxItems: z.number(),
  maxFetchPerDay: z.number(),
  lastSentAt: z.date().nullable(),
});

export type PulseSettings = z.infer<typeof PulseSettingsSchema>;

export const PulseStatsSchema = z.object({
  totalFollowed: z.number(),
  freshWithinWindow: z.number(),
  staleCount: z.number(),
  allFresh: z.boolean(),
  missingProfileCount: z.number(),
  thresholdHours: z.number(),
  thresholdSince: z.date(),
  evaluatedAt: z.date(),
});

export type PulseStats = z.infer<typeof PulseStatsSchema>;

export const PulseSyncStatusSchema = z
  .object({
    status: z.string(),
    lastSyncAttempt: z.date().nullable(),
    lastSuccessfulSync: z.date().nullable(),
    lastError: z.string().nullable(),
    updatedAt: z.date().nullable(),
    nextEligibleAt: z.date().nullable(),
  })
  .nullable();

export type PulseSyncStatus = z.infer<typeof PulseSyncStatusSchema>;

export const PulseRequestedBySchema = z
  .object({
    userId: z.string(),
    userName: z.string().optional(),
  })
  .nullable()
  .optional();

export type PulseRequestedBy = z.infer<typeof PulseRequestedBySchema>;

export const PulseJobSchema = z
  .object({
    id: z.string(),
    status: z.string(),
    startedAt: z.date().nullable(),
    finishedAt: z.date().nullable(),
    updatedAt: z.date().nullable(),
    outcome: z.string().nullable(),
    fetchedProfiles: z.number().nullable(),
    shouldSync: z.boolean().nullable(),
    error: z.string().nullable(),
    durationMs: z.number().nullable(),
    requestedBy: PulseRequestedBySchema,
  })
  .nullable();

export type PulseJob = z.infer<typeof PulseJobSchema>;

export const PulseSettingsResponseSchema = z.object({
  settings: PulseSettingsSchema,
  stats: PulseStatsSchema,
  syncStatus: PulseSyncStatusSchema,
  job: PulseJobSchema,
});

export type PulseSettingsResponse = z.infer<typeof PulseSettingsResponseSchema>;

export const PulseRefreshStatsSchema = z.object({
  totalFollowed: z.number(),
  freshWithinWindow: z.number(),
  staleCount: z.number(),
  thresholdHours: z.number(),
});

export type PulseRefreshStats = z.infer<typeof PulseRefreshStatsSchema>;

export const PulseRefreshResponseSchema = z.object({
  success: z.boolean(),
  jobId: z.string(),
  status: z.string(),
  message: z.string().optional(),
  stats: PulseRefreshStatsSchema.optional(),
});

export type PulseRefreshResponse = z.infer<typeof PulseRefreshResponseSchema>;

export const PulseSettingsUpdateSchema = z.object({
  enabled: z.boolean().optional(),
  emailEnabled: z.boolean().optional(),
  scheduleHour: z.number().int().min(0).max(23).optional(),
  maxItems: z.number().int().min(1).max(50).optional(),
  maxFetchPerDay: z.number().int().min(1).max(1).optional(),
});

export type PulseSettingsUpdate = z.infer<typeof PulseSettingsUpdateSchema>;

export const PulseSettingsUpdateSuccessSchema = z.object({
  success: z.literal(true),
  settings: PulseSettingsSchema,
});

export type PulseSettingsUpdateSuccess = z.infer<typeof PulseSettingsUpdateSuccessSchema>;

export const PulseSettingsUpdateErrorSchema = z.object({
  success: z.literal(false),
  error: z.string(),
});

export type PulseSettingsUpdateError = z.infer<typeof PulseSettingsUpdateErrorSchema>;

export const PulseSkipReasonSchema = z.enum([
  'disabled',
  'no-email',
  'no-observer',
  'no-items',
  'recently-sent',
  'outside-window',
]);

export type PulseSkipReason = z.infer<typeof PulseSkipReasonSchema>;

export const PulseEmailPinnedTweetSchema = z.object({
  id: z.string(),
  url: z.string(),
  viewUrl: z.string(),
  text: z.string(),
  isTextTruncated: z.boolean(),
  authorName: z.string(),
  authorUsername: z.string(),
  authorAvatarUrl: z.string().nullable().optional(),
  metrics: z.object({
    likes: z.number(),
    reposts: z.number(),
    replies: z.number(),
    bookmarks: z.number().nullable().optional(),
  }),
});

export type PulseEmailPinnedTweet = z.infer<typeof PulseEmailPinnedTweetSchema>;

export const PulseEmailItemSchema = z.object({
  profile: z.object({
    id: z.string(),
    name: z.string(),
    username: z.string(),
    avatar: z.string().nullable().optional(),
    verifiedType: PulseVerifiedTypeSchema,
    followers: z.number(),
    following: z.number(),
  }),
  change: z.object({
    labels: z.array(z.string()),
    detectedAt: z.date(),
    pinnedChanged: z.boolean(),
    pinnedTweetId: z.string().nullable().optional(),
    pinnedUrl: z.string().nullable().optional(),
    pinnedShowMoreUrl: z.string().nullable().optional(),
    pinnedTweet: PulseEmailPinnedTweetSchema.nullable().optional(),
    website: z
      .object({
        previous: z.string().nullable().optional(),
        current: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  score: z.number(),
});

export type PulseEmailItem = z.infer<typeof PulseEmailItemSchema>;

export const PulseSendTestResponseSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('sent'),
    subject: z.string(),
    count: z.number(),
    messageId: z.string().nullable(),
    text: z.string(),
    items: z.array(PulseEmailItemSchema),
  }),
  z.object({
    status: z.literal('dry-run'),
    subject: z.string(),
    count: z.number(),
    html: z.string(),
    text: z.string(),
    items: z.array(PulseEmailItemSchema),
  }),
  z.object({
    status: z.literal('skipped'),
    reason: PulseSkipReasonSchema,
    items: z.array(PulseEmailItemSchema).optional(),
  }),
  z.object({
    status: z.literal('error'),
    error: z.string(),
  }),
]);

export type PulseSendTestResponse = z.infer<typeof PulseSendTestResponseSchema>;

export const PulseSendTestRequestSchema = z.object({
  dryRun: z.boolean().optional(),
  recipient: z.string().email().optional(),
});

export type PulseSendTestRequest = z.infer<typeof PulseSendTestRequestSchema>;

export const PulseSearchTweetHitSchema = z.object({
  type: z.literal('tweet'),
  id: z.string(),
  text: z.string(),
  authorId: z.string(),
  authorUsername: z.string(),
  authorName: z.string(),
  authorProfileImageUrl: z.string().nullable().optional(),
  authorVerifiedType: PulseVerifiedTypeSchema.default('none'),
  createdAt: z.date(),
  likeCount: z.number().int().min(0),
  retweetCount: z.number().int().min(0),
  replyCount: z.number().int().min(0),
  quoteCount: z.number().int().min(0).optional(),
  bookmarkCount: z.number().int().min(0).optional(),
  source: z.enum(['postgres', 'hybrid', 'semantic']).optional(),
  full: z.unknown().optional(),
});

export type PulseSearchTweetHit = z.infer<typeof PulseSearchTweetHitSchema>;

export const PulseSearchProfileHitSchema = z.object({
  type: z.literal('user'),
  id: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable().optional(),
  verifiedType: PulseVerifiedTypeSchema.default('none'),
  source: z.enum(['database', 'twitter']).optional(),
});

export type PulseSearchProfileHit = z.infer<typeof PulseSearchProfileHitSchema>;

const PulseSearchTweetFiltersSchema = z.object({
  authorId: z.string().optional(),
  dateRange: z
    .object({
      since: z.string().optional(),
      until: z.string().optional(),
    })
    .optional(),
  metrics: z
    .object({
      minLikes: z.number().int().min(0).optional(),
      minRetweets: z.number().int().min(0).optional(),
    })
    .optional(),
  lang: z.string().optional(),
  hasMedia: z.boolean().optional(),
  hasLinks: z.boolean().optional(),
  url: z.string().trim().min(1).optional(),
});

export const PulseSearchCapabilitySchema = z.discriminatedUnion('resource', [
  z.object({
    resource: z.literal('tweets'),
    source: z.enum(['postgres', 'hybrid', 'semantic']).default('postgres'),
    limit: z.number().int().min(1).max(100).default(25),
    offset: z.number().int().min(0).default(0),
    detail: z.enum(['summary', 'full']).default('summary'),
    includeRetweets: z.boolean().default(true),
    filters: PulseSearchTweetFiltersSchema.optional(),
  }),
  z.object({
    resource: z.literal('profiles'),
    limit: z.number().int().min(1).max(50).default(5),
    includeRemote: z.boolean().default(true),
  }),
]);

export type PulseSearchCapability = z.infer<typeof PulseSearchCapabilitySchema>;

export const PulseSearchRequestSchema = z.object({
  query: z.string().trim().min(1),
  capabilities: z.array(PulseSearchCapabilitySchema).min(1),
});

export type PulseSearchRequest = z.infer<typeof PulseSearchRequestSchema>;

export const PulseSearchResponseSchema = z.object({
  query: z.string(),
  tweets: z
    .object({
      items: z.array(PulseSearchTweetHitSchema),
      pagination: PaginationOffsetSchema.extend({
        limit: PaginationOffsetSchema.shape.limit.min(0),
        offset: PaginationOffsetSchema.shape.offset.min(0),
      }).pick({ limit: true, offset: true }),
      nextOffset: z.number().int().min(0).nullable().optional(),
      source: z.enum(['postgres', 'hybrid', 'semantic']),
      total: z.number().int().min(0).optional(),
    })
    .optional(),
  profiles: z
    .object({
      items: z.array(PulseSearchProfileHitSchema),
      pagination: PaginationOffsetSchema.extend({
        limit: PaginationOffsetSchema.shape.limit.min(0),
        offset: PaginationOffsetSchema.shape.offset.min(0),
      }).partial({ offset: true }),
      source: z.enum(['database', 'mixed']).default('database'),
      total: z.number().int().min(0).optional(),
    })
    .optional(),
  diagnostics: z
    .object({
      tookMs: z.number().min(0).optional(),
      steps: z
        .array(
          z.object({
            resource: z.string(),
            source: z.string(),
            tookMs: z.number().min(0).optional(),
          })
        )
        .optional(),
    })
    .optional(),
});

export type PulseSearchResponse = z.infer<typeof PulseSearchResponseSchema>;
